import { useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from './Modal';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onParse: (text: string) => void;
}

export function ImportModal({
  isOpen,
  onClose,
  onParse,
}: ImportModalProps) {
  const { t } = useLanguage();
  const [text, setText] = useState('');

  const handleParse = () => {
    if (!text.trim()) return;
    onParse(text);
  };

  const handleClose = () => {
    setText('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('checklists.importItems', 'Import Items')}
      icon={Download}
      iconColor="primary"
      size="lg"
    >
      <div className="space-y-4">
        {/* Instructions banner */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-sm text-blue-800">
            {t('checklists.importHint', 'Open your IKEA shopping list PDF, select all text (Ctrl+A), copy it (Ctrl+C), and paste below.')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.pasteText', 'Paste text here')}
          </label>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t('checklists.importPlaceholder', 'Paste text containing IKEA product codes (XXX.XXX.XX)...')}
            rows={10}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow font-mono text-sm resize-none"
          />
        </div>

        <ModalFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleParse}
            disabled={!text.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {t('checklists.parseText', 'Parse Text')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
